import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Paper,
  useMediaQuery,
  useTheme,
  Switch,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Person as PersonIcon,
  SmartToy as SmartToyIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useRole } from '../contexts/RoleContext';
import { useSubscription } from '../contexts/SubscriptionContext';


// Sports mega-dropdown data, mirroring the demo's Sports menu:
// every sport individualized, with its sections under it.
const SPORT_MENU = [
  { name: 'NFL', slug: 'nfl' },
  { name: 'NBA', slug: 'nba' },
  { name: 'MLB', slug: 'mlb' },
  { name: 'NHL', slug: 'nhl' },
  { name: 'Soccer', slug: 'soccer' },
  { name: 'NCAAF', slug: 'ncaaf' },
  { name: 'NCAAB', slug: 'ncaab' },
  { name: 'WNBA', slug: 'wnba' },
  { name: 'UFC', slug: 'ufc' },
  { name: 'Boxing', slug: 'boxing' },
  { name: 'Tennis', slug: 'tennis' },
  { name: 'Golf', slug: 'golf' },
];

const SPORT_CATEGORIES = [
  { label: 'Overview', href: (slug) => `/sport/${slug}/overview` },
  { label: 'Scores', href: (slug) => `/sport/${slug}/scores` },
  { label: 'Schedule', href: (slug) => `/sport/${slug}/schedule` },
  { label: 'Odds', href: (slug) => `/sport/${slug}/odds` },
  { label: 'Futures', href: (slug) => `/sport/${slug}/futures` },
  { label: 'Props', href: (slug) => `/sport/${slug}/props` },
  { label: 'News', href: (slug) => `/sport/${slug}/news` },
  { label: 'Teams', href: (slug) => `/sport/${slug}/teams` },
];

const SportsMegaMenu = ({ open, onNavigate, onMouseEnter, onMouseLeave }) => {
  if (!open) return null;

  return (
    <Paper
      elevation={8}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: 'fixed',
        top: 84,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(1120px, calc(100vw - 32px))',
        maxHeight: 'calc(100vh - 110px)',
        overflowY: 'auto',
        p: 2.25,
        zIndex: (theme) => theme.zIndex.drawer + 2,
        color: 'text.primary',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pb: 1.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1 }}>
            Sports
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, fontSize: '0.68rem' }}
          >
            Choose a league, then open the exact section you need.
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => onNavigate(null)} aria-label="Close sports menu">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
          gap: 1.25,
          mt: 1.75,
        }}
      >
        {SPORT_MENU.map((sport) => (
          <Box key={sport.slug} sx={{ bgcolor: 'action.hover', p: 1.5 }}>
            <Button
              onClick={() => onNavigate(`/sport/${sport.slug}`)}
              sx={{
                justifyContent: 'flex-start',
                width: '100%',
                p: 0,
                pb: 1,
                mb: 1,
                borderRadius: 0,
                borderBottom: '2px solid #1d5c3f',
                color: '#FFFFFF',
                fontWeight: 800,
                fontSize: '1.05rem',
                textTransform: 'none',
              }}
            >
              {sport.name}
            </Button>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
              {SPORT_CATEGORIES.map((cat) => (
                <Button
                  key={cat.label}
                  onClick={() => onNavigate(cat.href(sport.slug))}
                  sx={{
                    justifyContent: 'flex-start',
                    px: 0.5,
                    py: 0.75,
                    minWidth: 0,
                    color: '#FFFFFF',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  {cat.label}
                </Button>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const HeaderNav = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, logout } = useAuth();
  const { theme: themeMode, setTheme } = useSettings();
  // The bar follows the app theme: green in dark mode, the same blue as the
  // left navigation bar (primary) in light mode; white content in both.
  const isDarkBar = themeMode !== 'light';
  const bar = {
    bg: isDarkBar ? '#1d5c3f' : '#26275b',
    text: '#ffffff',
    textMuted: '#e7f0eb',
    wordmark: 'white',
    chipBg: 'rgba(255, 255, 255, 0.08)',
    chipBorder: 'rgba(255, 255, 255, 0.25)',
    chipHover: 'rgba(255, 255, 255, 0.16)',
  };
  const { isAdmin, isHandicapper, isHandicapperOrAdmin } = useRole();
  const { isPremium, loading: subscriptionLoading } = useSubscription();
  const [sportsMenuOpen, setSportsMenuOpen] = useState(false);
  const sportsMenuTimer = useRef(null);

  useEffect(() => {
    setSportsMenuOpen(false);
  }, [location.pathname]);

  const openSportsMenu = () => {
    if (sportsMenuTimer.current) clearTimeout(sportsMenuTimer.current);
    setSportsMenuOpen(true);
  };

  const scheduleCloseSportsMenu = () => {
    if (sportsMenuTimer.current) clearTimeout(sportsMenuTimer.current);
    sportsMenuTimer.current = setTimeout(() => setSportsMenuOpen(false), 150);
  };


  const getNavigationItems = () => {
    const baseItems = [
      { path: '/', label: 'Home' },
      { path: '/odds', label: 'All Odds' },
      { path: '/allsports', label: 'All Sports' },
      { path: '/promos', label: 'Promotions' },
      { path: '/handicappers', label: 'Bettor Picks' },
      { path: '/resources', label: 'Resources' },
      // { path: '/live-games', label: 'Live' }, // Commented out
    ];

    // Add role-specific items
    if (isHandicapperOrAdmin) {
      baseItems.push({ path: '/handicapper-dashboard', label: 'My Picks' });
    }
    if (isAdmin) {
      baseItems.push({ path: '/admin', label: 'Admin Dashboard' });
      baseItems.push({ path: '/bettor-ai', label: 'BettorAI' });
    }

    return baseItems;
  };

  const navigationItems = getNavigationItems();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleThemeToggle = () => {
    const newTheme = themeMode === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  const handleAuthAction = () => {
    if (currentUser) {
      navigate('/account');
    } else {
      navigate('/login');
    }
  };

  const handleSignUp = () => {
    navigate('/login?signup=true');
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: bar.bg,
        color: bar.text,
        boxShadow: '0 8px 28px rgba(14, 36, 27, 0.18)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 60, sm: 72 }, px: { xs: 1, sm: 2 } }}>
        {/* Mobile Menu Button (Left) */}
        {isMobile && (
          <IconButton
            color="inherit"
            onClick={onMenuToggle}
            edge="start"
            sx={{
              mr: 1,
              bgcolor: bar.chipBg,
              border: `1px solid ${bar.chipBorder}`,
              '&:hover': { bgcolor: bar.chipHover },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo/Brand */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontStyle: 'italic',
            fontFamily: 'Impact, Arial, sans-serif',
            fontSize: { xs: '1rem', sm: '1.25rem' },
            fontWeight: 600,
            color: bar.wordmark,
            cursor: 'pointer',
            mr: { xs: 1, md: 4 },
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          onClick={() => handleNavigation('/')}
        >
          Bettor Advantage
        </Typography>

        {/* Desktop Navigation Links */}
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 0.5, flexGrow: 1, overflow: 'hidden' }}>
            {navigationItems.map((item) => {
              const navButton = (
                <Button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  endIcon={item.path === '/allsports' ? <ExpandMoreIcon sx={{ fontSize: 16, ml: -0.5 }} /> : undefined}
                  sx={{
                    color: isActive(item.path) ? bar.text : bar.textMuted,
                    px: { md: 1.5, lg: 2 },
                    py: 1,
                    minHeight: 44,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    borderBottom: isActive(item.path) ? '2px solid #e8721f' : '2px solid transparent',
                    borderRadius: 0,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: bar.text,
                      borderBottom: '2px solid #e8721f',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {item.label}
                </Button>
              );

              if (item.path === '/allsports') {
                return (
                  <Box
                    key={item.path}
                    onMouseEnter={openSportsMenu}
                    onMouseLeave={scheduleCloseSportsMenu}
                    sx={{ display: 'flex' }}
                  >
                    {navButton}
                  </Box>
                );
              }

              return navButton;
            })}
          </Box>
        )}

        {/* Sports mega-dropdown (desktop hover) */}
        {!isMobile && (
          <SportsMegaMenu
            open={sportsMenuOpen}
            onNavigate={(path) => {
              setSportsMenuOpen(false);
              if (path) handleNavigation(path);
            }}
            onMouseEnter={openSportsMenu}
            onMouseLeave={scheduleCloseSportsMenu}
          />
        )}

        {/* Spacer to push right side items to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right Side: Theme Toggle & Auth Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0, ml: 'auto' }}>
          {/* Theme Toggle */}
          <IconButton
            onClick={handleThemeToggle}
            color="inherit"
            size="small"
            sx={{
              color: bar.text,
              bgcolor: bar.chipBg,
              border: `1px solid ${bar.chipBorder}`,
              '&:hover': { bgcolor: bar.chipHover },
            }}
          >
            {themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          {/* Auth Buttons */}
          {!currentUser && (
            <>
              {/* Show buttons on desktop, icon on mobile */}
              {!isMobile ? (
                <>
                  <Button
                    onClick={handleAuthAction}
                    variant="contained"
                    sx={{
                      bgcolor: 'warning.main',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: { md: 2, lg: 3 },
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      '&:hover': {
                        bgcolor: '#ea580c',
                      },
                    }}
                  >
                    Log In
                  </Button>
                  <Button
                    onClick={handleSignUp}
                    variant="contained"
                    sx={{
                      bgcolor: 'secondary.main',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: { md: 2, lg: 3 },
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      '&:hover': {
                        bgcolor: '#22c55e',
                      },
                    }}
                  >
                    Sign Up
                  </Button>
                </>
              ) : (
                <IconButton
                  onClick={handleAuthAction}
                  sx={{
                    color: 'warning.main',
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <PersonIcon />
                </IconButton>
              )}
            </>
          )}

          {currentUser && (
            <>
              {/* Premium Upgrade Button - Desktop */}
              {!isMobile && !isPremium && !subscriptionLoading && (
                <Button
                  onClick={() => navigate('/subscription')}
                  variant="contained"
                  startIcon={<StarIcon />}
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { md: 1.5, lg: 2 },
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    mr: 1,
                    '&:hover': {
                      bgcolor: '#22c55e',
                    },
                  }}
                >
                  Upgrade
                </Button>
              )}
              {!isMobile ? (
                <Button
                  onClick={handleAuthAction}
                  variant="contained"
                  sx={{
                    bgcolor: 'warning.main',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { md: 2, lg: 3 },
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: '#ea580c',
                    },
                  }}
                >
                  My Account
                </Button>
              ) : (
                <IconButton
                  onClick={handleAuthAction}
                  sx={{
                    color: 'warning.main',
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <PersonIcon />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default HeaderNav;
